import { ReactNode } from 'react';
import { cn } from '@/lib/utils/cn';
import IcReorder from '@/assets/icons/ic-reorder.svg';
import { StanTag } from '@/components';
import { PageStatus } from '@/types/store';
import { ProductActionsMenu, type ProductActionsMenuProps } from './ProductActionsMenu';
import { SectionActionsMenu, type SectionActionsMenuProps } from './SectionActionsMenu';
import type { ProductItem, SectionItem } from '../types/myStore.types';

interface BaseMyStoreItemProps {
    title: string;
    className?: string;
}

interface ProductVariantProps extends BaseMyStoreItemProps, Omit<ProductActionsMenuProps, 'item'> {
    variant: 'product';
    item: ProductItem;
    iconUrl?: string;
    price?: string | number;
    badgeIcon?: ReactNode;
    status?: number;
    onClick?: () => void;
}

interface SectionVariantProps extends BaseMyStoreItemProps, Omit<SectionActionsMenuProps, 'item'> {
    variant: 'section';
    item: SectionItem;
}

export type MyStoreItemProps = ProductVariantProps | SectionVariantProps;

export function MyStoreItem(props: MyStoreItemProps) {
    const { variant, title, className } = props;

    if (variant === 'section') {
        const { item, onEdit, onDelete } = props;

        return (
            <div className={cn('flex w-full items-center gap-3 p-4', className)}>
                <div className='text-gray flex size-5 items-center justify-center'>
                    <IcReorder className='size-5 [&_path]:stroke-[2.5]' />
                </div>

                <h2 className='text-dark flex-1 text-base font-semibold'>{title}</h2>

                <div
                    className='text-gray flex flex-shrink-0 items-center justify-center p-2'
                    onPointerDown={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                >
                    <SectionActionsMenu item={item} onEdit={onEdit} onDelete={onDelete} />
                </div>
            </div>
        );
    }
    const {
        item,
        iconUrl,
        price,
        badgeIcon,
        status,
        onClick,
        onCopyUrl,
        onEdit,
        onUnpublish,
        onDuplicate,
        onMoveToLanding,
        onMoveToStore,
        onAnalytics,
        onDelete,
        onAffiliateAnalytics,
    } = props;

    const isDraft = status === PageStatus.DRAFT;

    return (
        <div
            className={cn(
                'bg-canvas my-1 flex w-full items-center gap-3 rounded-md p-4 shadow-sm transition-shadow',
                onClick && 'cursor-pointer hover:shadow-md',
                className
            )}
            onClick={onClick}
        >
            <div className='text-gray flex size-5 items-center justify-center'>
                <IcReorder className='size-5 [&_path]:stroke-[2.5]' />
            </div>

            {iconUrl && (
                <div className='bg-brand-subtle flex size-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-md'>
                    <img src={iconUrl} alt='' className='size-full object-cover' />
                </div>
            )}

            <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                <div className='flex items-center gap-2'>
                    <span className='text-dark truncate font-medium'>{title}</span>
                    {badgeIcon && <span className='text-gray flex-shrink-0'>{badgeIcon}</span>}
                </div>
                {price !== undefined && (
                    <span className='text-secondary text-sm'>
                        ${typeof price === 'number' ? price.toFixed(2) : price}
                    </span>
                )}
            </div>

            {isDraft && <StanTag label='Draft' />}

            <div
                className='text-gray flex flex-shrink-0 items-center justify-center p-2'
                onPointerDown={e => e.stopPropagation()}
                onMouseDown={e => e.stopPropagation()}
            >
                <ProductActionsMenu
                    item={item}
                    onCopyUrl={onCopyUrl}
                    onEdit={onEdit}
                    onUnpublish={onUnpublish}
                    onDuplicate={onDuplicate}
                    onMoveToLanding={onMoveToLanding}
                    onMoveToStore={onMoveToStore}
                    onAnalytics={onAnalytics}
                    onDelete={onDelete}
                    onAffiliateAnalytics={onAffiliateAnalytics}
                />
            </div>
        </div>
    );
}
